/**
 * Fixit Trigger
 *
 * This module is a wrapper around born-fixit to allow the use of [data-fixit-trigger]
 * attribute on any element.
 *
 * see ../helpers/modules.js for "fixit" module initialization
 */

import Fixit from '@borngroup/born-fixit';
import { callbackOnElements } from '@borngroup/born-utilities';
import { breakpointRanges, objectAssign } from '../helpers/utilities';

export default class FixitTrigger {
    constructor(options) {
        options.settings = options.settings || {};
        this.settingsAttribute = options.settings.settingsAttribute || 'data-fixit-trigger';

        callbackOnElements(options.elements, this.setupFixitElements.bind(this, options.settings));
    }

    /**
     * Parses the provided `fixitNode` element and tries to find a `this.settingsAttribute` configuration.
     * The configuration is parsed and merged against an optional `settings` object that can be passed to the constructor.
     */
    setupFixitElements(settings, fixitNode) {
        let parsedOptions = JSON.parse(fixitNode.getAttribute(this.settingsAttribute)) || {};

        // In this module scenario, target will always be fixitNode.
        parsedOptions.target = fixitNode;
        parsedOptions.enabled = parsedOptions.enabled ? FixitTrigger.isEnabled.bind(this, parsedOptions.enabled) : undefined;

        parsedOptions.respondToParent = this.getElement(parsedOptions.respondToParent);
        parsedOptions.containedInParent = this.getElement(parsedOptions.containedInParent);

        fixitNode.removeAttribute(this.settingsAttribute);

        new Fixit(objectAssign({}, settings, parsedOptions));

        if (parsedOptions.dynamicOffset) {
            this.setupDynamicOffset(fixitNode, parsedOptions.dynamicOffset);
        }
    }

    getElement(elSelector) {
        return typeof elSelector === 'string' ? document.querySelector(elSelector) : elSelector;
    }

    /**
     * Configure a dynamic offset element,
     * which uses FixIt's API to toggle the offset value in the current fixit-trigger node.
     */
    setupDynamicOffset(fixitNode, offsetSelector) {
        this.getElement(offsetSelector).addEventListener('fixit:scrollDirectionUpdate', function(evt) {
            let currentInstance = Fixit.getInstance(fixitNode);

            if (!currentInstance.isFrozen && !currentInstance.isDocked) {
                if (evt.detail.newDirection === 'up' && evt.detail.previousDirection) {
                    currentInstance.setOffsetElements(offsetSelector);

                    currentInstance.setOffsetValue();
                } else {
                    currentInstance.offset = 0;
                }

                currentInstance.setTargetPos(currentInstance.offset === 0);
            }
        });
    }

    /**
     * Determines wether or not Fixit should be enabled.
     * This method is called within Fixit on init and resize.
     * Leverages `breakpointRanges` to match agains the `breakpointName` value.
     * @return {Boolean}                [description]
     */
    static isEnabled(breakpointName) {
        let matchedBreakpoint = breakpointRanges[breakpointName];

        return matchedBreakpoint && (!matchedBreakpoint.min || document.body.offsetWidth >= matchedBreakpoint.min) && (!matchedBreakpoint.max || document.body.offsetWidth <= matchedBreakpoint.max);
    }
}
